import type { ChangeEvent, ReactNode } from "react";
import { forwardRef, useId } from "react";

import { Icon } from "@core/components/Icon";
import { SupportingText } from "@core/components/SupportingText";
import type { SafeInputHtmlAttributes } from "@core/types/SafeHtmlAttributes";

import { UiCheckIcon } from "@jti/brand-icons/ui-check";

import styles from "./Checkbox.module.scss";

type CheckboxNativeAttributes = Omit<SafeInputHtmlAttributes, "onChange" | "className" | "type">;

export type CheckboxProps = {
  label: ReactNode;
  checked?: boolean;
  disabled?: boolean;
  status?: "default" | "error";
  errorMessage?: string;
  helperText?: string;
  onCheckedChange?: (checked: boolean) => void;
} & CheckboxNativeAttributes;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      checked,
      disabled = false,
      status = "default",
      errorMessage,
      helperText,
      id,
      onCheckedChange,
      "aria-describedby": ariaDescribedBy,
      ...rest
    },
    ref
  ) => {
    const reactId = useId();
    const checkboxId = id ?? `checkbox-${reactId}`;
    const hasError = status === "error";
    const supportingText = errorMessage ?? helperText;
    const supportingTextId = supportingText ? `${checkboxId}-supporting-text` : undefined;
    const mergedDescribedBy = [ariaDescribedBy, supportingTextId].filter(Boolean).join(" ") || undefined;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(event.currentTarget.checked);
    };

    return (
      <div className={`${styles.checkbox} ${disabled ? styles.disabled : ""}`}>
        <label className={styles.controlRow} htmlFor={checkboxId}>
          <input
            {...rest}
            id={checkboxId}
            ref={ref}
            className={styles.input}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={mergedDescribedBy}
            onChange={handleChange}
          />
          <span className={styles.control} aria-hidden="true">
            <Icon className={styles.checkIcon} icon={UiCheckIcon} size="xs" />
          </span>
          <div className={styles.label}>{label}</div>
        </label>

        {supportingText && (
          <div className={styles.supportingText}>
            <SupportingText id={supportingTextId} variant={hasError ? "error" : "info"}>
              {supportingText}
            </SupportingText>
          </div>
        )}
      </div>
    );
  }
);
